// legacyStrikesStorage — A-097: legacy localStorage-baserad strike-/ban-
// hantering extraherad ur `AiAssistantDialog.tsx` (rader 87-127). Modulen
// är aktiv ENDAST när feature-flag `moderationV2` är OFF — då används
// `useModerationStrikes` (DB-backed) istället. Legacy-pathen behålls som
// fallback fram till moderationV2 är 100 % rollout:ad.
//
// Notera: `resetLegacyLocalStorageStrikes` (i `src/hooks/useModerationStrikes.ts`)
// rensar samma keys som denna modul skriver — kör den vid moderationV2-
// upgrade så user inte sitter kvar med stale legacy-strike-count.
//
// Subset av A-087 (1992-rad AiAssistantDialog refactor). Lyfter ut
// strike-storage utan att kräva moderationV2-cutover, så framtida A-087-
// PR krymper med ~50 rader.

/** localStorage-key för antal strikes (legacy). */
export const LEGACY_STRIKES_KEY = 'engicoro-ai-strikes';

/** localStorage-key för ban-flag (legacy). */
export const LEGACY_BAN_KEY = 'engicoro-ai-banned';

/** Antal strikes innan ban (legacy-tröskel). DB-pathen har egen MAX_STRIKES. */
export const LEGACY_MAX_STRIKES = 3;

/** Returnerar `true` om user är ban:ad i legacy-localStorage. */
export function isLegacyBanned(): boolean {
  return localStorage.getItem(LEGACY_BAN_KEY) === 'true';
}

/** Markera user som ban:ad i legacy-localStorage. */
export function markLegacyBanned(): void {
  localStorage.setItem(LEGACY_BAN_KEY, 'true');
}

/** Aktuellt antal legacy-strikes (0 om ingen rad). */
export function readLegacyStrikes(): number {
  return parseInt(localStorage.getItem(LEGACY_STRIKES_KEY) || '0', 10);
}

/**
 * Inkrementerar strike-count med 1 + persistar. Returnerar nya värdet så
 * caller kan jämföra mot `LEGACY_MAX_STRIKES` utan att läsa om från
 * localStorage.
 */
export function incrementLegacyStrike(): number {
  const current = readLegacyStrikes() + 1;
  localStorage.setItem(LEGACY_STRIKES_KEY, String(current));
  return current;
}

/**
 * Rensa legacy ban-flag + strike-count. Används vid moderationV2-rollout
 * + som developer-shortcut via window.__clearLegacyBan() i devtools.
 * Idempotent — no-op om nycklarna inte finns.
 */
export function clearLegacyBan(): void {
  try {
    localStorage.removeItem(LEGACY_STRIKES_KEY);
    localStorage.removeItem(LEGACY_BAN_KEY);
    localStorage.removeItem('engicoro-ai-ban'); // gammal typo-nyckel
  } catch {
    // Ignorera (privacy mode / no localStorage)
  }
}

// Expose dev-shortcut globalt så user kan kalla det från devtools utan
// att importera. Window-typen är `unknown` här — vi cast:ar till record
// för att undvika `any` + ESLint-protest. Bara client-side.
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).__clearLegacyBan = clearLegacyBan;
}
